import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, ChevronDown, Sparkles, Minus } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import CTABanner from "../components/homepage/CTABanner";
import FAQSection from "../components/homepage/FAQSection";
import PageHero from "../components/shared/PageHero";
import { MARKETS_CONFIG, MARKET_CODES } from "../data/markets";
import { PRICING } from "../data/site";

const COUNTRY_TO_MARKET = {
  US: "us", GB: "uk", AU: "au", AE: "uae", CA: "ca",
  DE: "de", JP: "jp", SG: "sg", BR: "br", IN: "in",
};

const COMPARE_ROWS = [
  { label: "ASO keyword research", starter: "1 store", growth: "iOS + Android", enterprise: "Multi-market" },
  { label: "Listing + metadata rewrite", starter: true, growth: true, enterprise: true },
  { label: "A/B tested screenshots", starter: "1 round", growth: "Continuous", enterprise: "Continuous × markets" },
  { label: "Ratings & reviews ops", starter: false, growth: true, enterprise: true },
  { label: "Apple Search Ads management", starter: false, growth: true, enterprise: true },
  { label: "Google UAC + Meta + TikTok", starter: false, growth: false, enterprise: true },
  { label: "GEO / AI citation", starter: false, growth: "Pilot", enterprise: "Full strategy" },
  { label: "Localization (markets)", starter: "1", growth: "3", enterprise: "15+" },
  { label: "Reporting cadence", starter: "Monthly memo", growth: "Weekly + Slack", enterprise: "Custom dashboard" },
  { label: "Support SLA", starter: "Email", growth: "Slack, 1 business day", enterprise: "Priority 24/7" },
];

const ADDONS = [
  { name: "Extra market localization", price: 349, cadence: "/market/month" },
  { name: "Additional screenshot A/B set", price: 249, cadence: "/round" },
  { name: "GEO / AI citation add-on", price: 699, cadence: "/month" },
  { name: "Review reply ops (high-volume)", price: 499, cadence: "/month" },
  { name: "Custom reporting dashboard", price: 999, cadence: "/one-time" },
];

function cellIcon(v) {
  if (v === true) return <Check className="w-4 h-4 text-electric mx-auto" strokeWidth={3} />;
  if (v === false) return <Minus className="w-4 h-4 text-zinc-300 mx-auto" />;
  return <span className="text-sm text-zinc-700">{v}</span>;
}

function formatLocal(usd, m) {
  const n = Math.round((usd * m.multiplier) / 10) * 10;
  return `${m.currency}${n.toLocaleString()}`;
}

export default function PricingPage() {
  const [code, setCode] = useState("us");
  const [loaded, setLoaded] = useState(false);
  const m = MARKETS_CONFIG[code];

  useEffect(() => {
    // Auto-detect country on first load
    const BACKEND = process.env.REACT_APP_BACKEND_URL;
    const saved = localStorage.getItem("lumotis_pricing_market");
    if (saved && MARKETS_CONFIG[saved]) {
      setCode(saved);
      setLoaded(true);
      return;
    }
    let cancelled = false;
    fetch(`${BACKEND}/api/geo`)
      .then((r) => r.json())
      .then((j) => {
        if (cancelled) return;
        const slug = COUNTRY_TO_MARKET[(j.country || "").toUpperCase()];
        if (slug) setCode(slug);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const onChange = (c) => {
    setCode(c);
    localStorage.setItem("lumotis_pricing_market", c);
  };

  return (
    <>
      <Helmet>
        <title>{`ASO Pricing — App Store Optimization Plans in ${m.currency.trim()} | Lumotis`}</title>
        <meta
          name="description"
          content={`Lumotis ASO pricing in ${m.name}. Starter, Growth and Enterprise plans in ${m.currency.trim()} (${m.locale.split("_")[1]}). Transparent, monthly, cancel anytime.`}
        />
        <meta
          name="keywords"
          content="ASO pricing, App Store Optimization pricing, app marketing agency pricing, Lumotis plans, ASO cost"
        />
        <link rel="canonical" href="https://lumotis.com/pricing" />
      </Helmet>
      <Navbar />
      <main data-testid="pricing-page">
        <PageHero
          testid="pricing-hero"
          eyebrow="Pricing"
          breadcrumb={[{ label: "Home", href: "/" }, { label: "Pricing" }]}
          title="Transparent ASO pricing,"
          titleAccent="in your currency."
          subtitle={`Starter, Growth and Enterprise — the same engagement models we run for 100+ apps across 15+ markets. Auto-detected for ${m.name}. Switch anytime.`}
        />

        {/* Country selector */}
        <section className="py-6 bg-white border-y border-zinc-200 sticky top-16 md:top-20 z-30">
          <div className="container flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="flex items-center gap-3">
              <span className="text-overline text-zinc-500">Show prices for</span>
              <div className="relative">
                <select
                  value={code}
                  onChange={(e) => onChange(e.target.value)}
                  data-testid="pricing-country-select"
                  aria-label="Select country for pricing"
                  className="appearance-none pl-4 pr-10 py-2.5 border border-charcoal bg-white text-sm font-semibold text-charcoal focus:border-electric outline-none"
                >
                  {MARKET_CODES.map((c) => (
                    <option key={c} value={c}>
                      {MARKETS_CONFIG[c].flag} {MARKETS_CONFIG[c].name} ({MARKETS_CONFIG[c].currency.trim()})
                    </option>
                  ))}
                </select>
                <ChevronDown className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-charcoal pointer-events-none" />
              </div>
              {loaded && (
                <span
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500"
                  data-testid="pricing-auto-detected"
                >
                  <Sparkles className="w-3 h-3 text-electric" /> auto-detected
                </span>
              )}
            </div>
            <Link
              to={`/${code}`}
              data-testid="pricing-market-link"
              className="inline-flex items-center gap-2 text-sm font-semibold text-charcoal hover:text-electric"
            >
              See {m.shortName} homepage <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Tiers */}
        <section className="py-20 md:py-24 bg-zinc-50 border-b border-zinc-200">
          <div className="container">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {PRICING.map((p, idx) => {
                const localPrice = p.price.startsWith("$")
                  ? formatLocal(parseInt(p.price.replace(/[^0-9]/g, ""), 10), m)
                  : p.price;
                return (
                  <motion.article
                    key={p.tier}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.06 }}
                    className={`relative p-8 md:p-10 flex flex-col ${
                      p.highlighted
                        ? "bg-charcoal text-white border border-charcoal -translate-y-2 shadow-xl shadow-electric/20 rounded-2xl"
                        : "bg-white border border-zinc-200"
                    }`}
                    data-testid={`pricing-tier-${p.tier.toLowerCase()}`}
                  >
                    {p.highlighted && (
                      <span className="absolute top-0 right-0 bg-electric text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1.5">
                        Most Popular
                      </span>
                    )}
                    <p
                      className={`text-overline ${p.highlighted ? "text-electric" : "text-zinc-500"} mb-3`}
                    >
                      {p.tier}
                    </p>
                    <div className="flex items-baseline gap-1">
                      <span
                        data-testid={`pricing-tier-${p.tier.toLowerCase()}-price`}
                        className="font-display text-5xl md:text-6xl font-black tracking-tight"
                      >
                        {localPrice}
                      </span>
                      {p.cadence && (
                        <span
                          className={`text-sm ${p.highlighted ? "text-zinc-400" : "text-zinc-500"}`}
                        >
                          {p.cadence}
                        </span>
                      )}
                    </div>
                    <p
                      className={`mt-4 text-sm ${p.highlighted ? "text-zinc-300" : "text-zinc-600"} leading-relaxed`}
                    >
                      {p.description}
                    </p>
                    <ul className="mt-8 space-y-3 flex-1">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-3 text-sm">
                          <Check
                            className="w-4 h-4 mt-0.5 text-electric flex-shrink-0"
                            strokeWidth={3}
                          />
                          <span
                            className={p.highlighted ? "text-zinc-200" : "text-zinc-700"}
                          >
                            {f}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <Link
                      to="/contact"
                      data-testid={`pricing-cta-${p.tier.toLowerCase()}`}
                      className={`mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 font-semibold transition-all ${
                        p.highlighted
                          ? "bg-electric text-white hover:bg-electric-400"
                          : "bg-charcoal text-white hover:bg-electric"
                      }`}
                    >
                      {p.cta} <ArrowRight className="w-4 h-4" />
                    </Link>
                  </motion.article>
                );
              })}
            </div>
            <p
              className="mt-8 text-center text-xs text-zinc-500"
              data-testid="pricing-rate-note"
            >
              Prices shown in {m.currency.trim()} at indicative local rates. Final invoice in {m.currency.trim()} or USD at client preference. VAT / GST extra where applicable.
            </p>
          </div>
        </section>

        {/* Compare table */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container">
            <div className="max-w-3xl mb-10">
              <p className="text-overline text-electric mb-3">02 — Compare</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                What's inside each plan, line by line.
              </h2>
            </div>
            <div className="overflow-x-auto border border-zinc-200" data-testid="pricing-compare-table">
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="bg-zinc-50 border-b border-zinc-200">
                    <th className="text-left p-4 text-overline text-zinc-500 w-[40%]">Feature</th>
                    <th className="p-4 text-overline text-zinc-500">Starter</th>
                    <th className="p-4 text-overline text-electric bg-charcoal text-white">
                      Growth
                    </th>
                    <th className="p-4 text-overline text-zinc-500">Enterprise</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE_ROWS.map((r, idx) => (
                    <tr
                      key={r.label}
                      className="border-b border-zinc-100 last:border-0"
                      data-testid={`pricing-compare-row-${idx}`}
                    >
                      <td className="p-4 text-sm text-charcoal font-medium">
                        {r.label}
                      </td>
                      <td className="p-4 text-center">{cellIcon(r.starter)}</td>
                      <td className="p-4 text-center bg-charcoal/[0.02]">
                        {cellIcon(r.growth)}
                      </td>
                      <td className="p-4 text-center">{cellIcon(r.enterprise)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Add-ons */}
        <section className="py-20 md:py-28 bg-zinc-50 border-y border-zinc-200">
          <div className="container">
            <div className="max-w-3xl mb-10">
              <p className="text-overline text-electric mb-3">03 — Add-ons</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-charcoal text-balance">
                Stack on top of any plan.
              </h2>
              <p className="mt-4 text-zinc-600">
                Prices localized to {m.name}. No long contracts — add or remove add-ons monthly.
              </p>
            </div>
            <ul className="bg-white border border-zinc-200 divide-y divide-zinc-200">
              {ADDONS.map((a, idx) => (
                <li
                  key={a.name}
                  className="flex items-center justify-between p-5 md:p-6 hover:bg-zinc-50 transition-colors"
                  data-testid={`pricing-addon-${idx}`}
                >
                  <span className="font-display text-lg font-bold text-charcoal">
                    {a.name}
                  </span>
                  <div className="flex items-center gap-6">
                    <span className="font-display text-xl font-black text-electric">
                      {formatLocal(a.price, m)}
                      <span className="text-xs text-zinc-500 font-medium ml-1">
                        {a.cadence}
                      </span>
                    </span>
                    <Link
                      to="/contact"
                      className="text-xs font-semibold text-charcoal hover:text-electric"
                    >
                      Add <ArrowRight className="inline w-3 h-3" />
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Guarantee / Close */}
        <section className="py-20 md:py-24 bg-charcoal text-white relative overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern-dark opacity-40 pointer-events-none" />
          <div className="container relative grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <p className="text-overline text-electric mb-3">Our promise</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold text-balance">
                No annual lock-ins. No agency double-speak. Just rank, measured weekly.
              </h2>
              <p className="mt-5 text-zinc-300 max-w-xl leading-relaxed">
                Every plan is month-to-month. If we don't move your rank inside
                90 days, you get a free month while we fix it. That's how
                confident we are in the engine.
              </p>
            </div>
            <div className="lg:col-span-5 bg-white text-charcoal p-8 shadow-xl shadow-electric/20 rounded-2xl">
              <p className="text-overline text-electric mb-3">Need something custom?</p>
              <p className="font-display text-2xl font-bold leading-snug">
                Multi-app portfolio, multi-market, or full GEO / app-dev engagement — we'll quote it.
              </p>
              <Link
                to="/contact"
                data-testid="pricing-enterprise-cta"
                className="mt-6 inline-flex items-center gap-2 bg-charcoal text-white px-5 py-3 font-semibold hover:bg-electric transition-colors"
              >
                Talk to sales <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        <CTABanner />
        <FAQSection />
      </main>
      <Footer />
    </>
  );
}
